/**
 * RebookBookingForm — the pre-submit body of the rebook drawer (BON-146):
 * notices, the workshop being left, the destination slot, the required reason,
 * and the footer.
 *
 * Split from `RebookBookingDrawer` because the drawer is three screens sharing a
 * frame (form, full-page picker, receipt) and only this one collects anything.
 */
import type { BookingWorkshop } from '../../api/bookingsV2';

import { RebookFormFooter } from './RebookFormFooter';
import { RebookMovingFromCard } from './RebookMovingFromCard';
import { RebookNoticeCard } from './RebookNoticeCard';
import type { RebookNotice } from './rebookNotices';
import { RebookReasonField } from './RebookReasonField';
import { RebookTargetChoice } from './RebookTargetChoice';
import type { PickerWorkshop } from './shared/pickerWorkshop';

export interface RebookBookingFormProps {
  workshop: BookingWorkshop;
  notices: RebookNotice[];
  blocked: boolean;
  overriding: boolean;
  target: PickerWorkshop | null;
  onBrowse: () => void;
  reason: string;
  onReasonChange: (v: string) => void;
  reasonError: string | null;
  reasonRef: React.RefObject<HTMLTextAreaElement>;
  overrideChecked: boolean;
  onOverrideChange: (v: boolean) => void;
  submitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

/** The explicit opt-in, rendered INSIDE the cutoff notice so the consequence and
 *  the consent are never separated. Available at every breakpoint — a phone is
 *  where a same-day move is most likely to be asked for, and an override that
 *  applied itself silently would be the one action here with no record of anyone
 *  choosing it. A plain function, not a component (`no-multi-comp`). */
function overrideCheckbox(checked: boolean, onChange: (v: boolean) => void) {
  return (
    <label className="mt-2.5 flex cursor-pointer items-start gap-2 text-xs font-semibold text-bark-900 dark:text-stone-100">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 flex-shrink-0 accent-amber-600"
      />
      Override the cutoff for this booking
    </label>
  );
}

export function RebookBookingForm(props: RebookBookingFormProps) {
  const { notices, target } = props;

  return (
    <form onSubmit={props.onSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
        {notices.length > 0 && (
          // Announced: a refusal arrives after the CSR presses Move booking, and
          // without a live region a screen-reader user hears nothing at all
          // while the button they were on goes disabled.
          <div role="alert" aria-live="assertive" className="space-y-2.5">
            {notices.map((n) => (
              <RebookNoticeCard key={n.id} notice={n}>
                {n.detail.kind === 'cutoffOverride'
                  ? overrideCheckbox(props.overrideChecked, props.onOverrideChange)
                  : null}
              </RebookNoticeCard>
            ))}
          </div>
        )}

        <RebookMovingFromCard workshop={props.workshop} />

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
            Move to
          </p>
          <RebookTargetChoice target={target} onBrowse={props.onBrowse} />
        </div>

        <RebookReasonField
          value={props.reason}
          onChange={props.onReasonChange}
          error={props.reasonError}
          inputRef={props.reasonRef}
        />
      </div>

      <RebookFormFooter
        blocked={props.blocked}
        overriding={props.overriding}
        hasTarget={target !== null}
        submitting={props.submitting}
        onCancel={props.onCancel}
      />
    </form>
  );
}
